import React from "react";
import { Breadcrumbs as MuiBreadcrumbs, Link, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";

export interface Crumb {
    label: string;
    /** Omit on the current page → rendered as a non-clickable leaf. */
    to?: string;
}

/**
 * Loose shape shared by the two project sources that feed a breadcrumb:
 * the full `BrandProject` on the project-detail page and the
 * `getBrandProject` payload fetched on the video-detail page.
 */
export type ProjectTrailInput =
    | {
          id?: number | string;
          title?: string | null;
          order?: {
              id?: number;
              name?: string;
              sow?: { id?: number; name?: string } | null;
          } | null;
      }
    | null
    | undefined;

/**
 * Builds the SOW › Order › Project trail for the Video Production hierarchy.
 * Each upper crumb is only added when its `{ id, name }` are present, so the
 * trail collapses gracefully (no order → just Project; no SOW → Order + Project).
 * Pass `projectHref` to make the Project crumb a link (video-detail page);
 * omit it to render Project as the non-clickable leaf (project-detail page).
 */
// `basePath` is threaded in from the caller rather than read from a hook:
// this is a plain function, not a component, so it cannot call usePortalPath
// itself. Callers pass useNetworkScope().basePath (TDC-1609); omitting it
// yields root-absolute crumbs, which is correct for internal pages.
export function buildProjectTrail(
    project: ProjectTrailInput,
    opts?: { projectHref?: string; basePath?: string }
): Crumb[] {
    const items: Crumb[] = [];
    const order = project?.order;
    const sow = order?.sow;
    const base = opts?.basePath || '';

    if (sow?.id != null && sow?.name) {
        items.push({ label: sow.name, to: `${base}/video-production/orders?sowId=${sow.id}` });
    }
    if (order?.id != null && order?.name) {
        items.push({ label: order.name, to: `${base}/video-production/projects?orderId=${order.id}` });
    }
    items.push({ label: project?.title || "Project", to: opts?.projectHref });

    return items;
}

/**
 * Lightweight breadcrumb bar built on MUI <Breadcrumbs>. Links navigate via
 * react-router; the leaf (last item, or any item without `to`) is plain text.
 * `maxItems` is pinned high so long trails wrap onto multiple lines rather than
 * collapsing to an ellipsis.
 */
export function Breadcrumbs({ items }: { items: Crumb[] }) {
    const navigate = useNavigate();
    if (!items.length) return null;

    return (
        <MuiBreadcrumbs
            separator="›"
            maxItems={100}
            aria-label="breadcrumb"
            sx={{
                "& .MuiBreadcrumbs-ol": { flexWrap: "wrap" },
                "& .MuiBreadcrumbs-separator": { color: "var(--text-2)", mx: 1 },
            }}
        >
            {items.map((item, i) => {
                const isLeaf = i === items.length - 1 || !item.to;
                return isLeaf ? (
                    <Typography
                        key={i}
                        sx={{ fontSize: 14, fontWeight: 450, color: "var(--text)" }}
                    >
                        {item.label}
                    </Typography>
                ) : (
                    <Link
                        key={i}
                        component="button"
                        underline="hover"
                        onClick={() => navigate(item.to as string)}
                        sx={{
                            fontSize: 14,
                            fontWeight: 450,
                            color: "var(--text-2)",
                            cursor: "pointer",
                            background: "none",
                            border: "none",
                            padding: 0,
                            lineHeight: "inherit",
                        }}
                    >
                        {item.label}
                    </Link>
                );
            })}
        </MuiBreadcrumbs>
    );
}
